import { ChargebeeDto } from '@gap/backend/app-services/chargebee/models/ChargebeeDto'
import { TierNamesDto } from '@gap/utils/tier/tier-config'
import { namingConfig } from '@gap/utils/config/naming-config'

/**
 * INTERIM (NO-455): the static plan catalogue for the upgrade plan picker, shaped exactly
 * like the Chargebee catalogue (`ChargebeeDto.PurchasableItem`) so `PlanGrid` /
 * `PlanSelection` render it unchanged. Prices are the regional pricing pages
 * (thegaphq.com/{nz,au,uk,cad,row}-pricing-sales, captured 2026-07-08), in the currency's
 * smallest unit (cents) — the Chargebee convention PlanGrid divides by 100.
 *
 * PR #471 (feature/323-chargebee-v1) replaces this file with the live Chargebee catalogue;
 * delete it when that merges.
 * 
 * Notion ticket 564 updated the pricing for elevate, as per 25sept 2026 untill Chargebee goes live
 */

const supportedCurrencies = ['NZD', 'AUD', 'GBP', 'CAD', 'USD'] as const
type SupportedCurrency = (typeof supportedCurrencies)[number]

/** Anything we don't price in falls back to USD, mirroring the Chargebee catalogue's fallback. */
const fallbackCurrency: SupportedCurrency = 'USD'

interface StaticPlanDefinition {
   /** Exact tier name — doubles as PurchasableItem.itemId so the store can map a card back to a tier. */
   tierName: TierNamesDto
   planType: 'individual' | 'team'
   description: string
   features: string[]
   priceInCents: Record<SupportedCurrency, number>
   addons: {
      name: string
      priceInCents: Record<SupportedCurrency, number>
   }[]
}

const staticPlanDefinitions: StaticPlanDefinition[] = [
   {
      tierName: 'Core',
      planType: 'individual',
      description: 'Includes 1 user',
      features: [
         `Meeting workspace (${namingConfig.strategist.singular}, ${namingConfig.meetingAssistant.singular}, Producer)`,
         'Custom meetings and templates',
         'Limited Gap Academy access',
      ],
      priceInCents: { NZD: 9500, AUD: 9500, GBP: 4500, CAD: 8500, USD: 6500 },
      addons: [{ name: 'Additional Core user', priceInCents: { NZD: 9500, AUD: 9500, GBP: 4500, CAD: 8500, USD: 6500 } }],
   },
   {
      tierName: 'Grow',
      planType: 'individual',
      description: 'Includes 1 user',
      features: [
         'Everything in Core',
         'Coach',
         'Value Gap Calculator',
         'Proposals',
         'Full Gap Academy access',
      ],
      priceInCents: { NZD: 17500, AUD: 17500, GBP: 8500, CAD: 15500, USD: 11500 },
      addons: [{ name: 'Additional Grow user', priceInCents: { NZD: 17500, AUD: 17500, GBP: 8500, CAD: 15500, USD: 11500 } }],
   },
   {
      tierName: 'Expand',
      planType: 'team',
      description: 'Includes 2 Grow + 1 Core users',
      features: ['Everything in Grow'],
      priceInCents: { NZD: 42500, AUD: 42500, GBP: 20500, CAD: 37500, USD: 27500 },
      addons: [
         { name: 'Additional Grow user', priceInCents: { NZD: 16500, AUD: 16500, GBP: 8000, CAD: 15500, USD: 10500 } },
         { name: 'Additional Core user', priceInCents: { NZD: 8500, AUD: 8500, GBP: 4000, CAD: 8000, USD: 6000 } },
      ],
   },
   {
      tierName: 'Accelerate',
      planType: 'team',
      description: 'Includes 5 Grow users',
      features: ['Everything in Grow', 'Dedicated onboarding support'],
      priceInCents: { NZD: 69500, AUD: 69500, GBP: 34500, CAD: 62500, USD: 46500 },
      addons: [
         { name: 'Additional Grow user', priceInCents: { NZD: 14000, AUD: 14000, GBP: 7000, CAD: 12500, USD: 9500 } },
         { name: 'Additional Core user', priceInCents: { NZD: 8000, AUD: 8000, GBP: 3500, CAD: 7000, USD: 5500 } },
      ],
   },
   {
      tierName: 'Elevate',
      planType: 'team',
      description: 'Includes 10 Grow users',
      features: ['Everything in Grow', 'Dedicated onboarding support'],
      priceInCents: { NZD: 99500, AUD: 99500, GBP: 49500, CAD: 92500, USD: 65500 },
      addons: [
         { name: 'Additional Grow user', priceInCents: { NZD: 9500, AUD: 9500, GBP: 4500, CAD: 9500, USD: 6500 } },
         { name: 'Additional Core user', priceInCents: { NZD: 5500, AUD: 5500, GBP: 2500, CAD: 5000, USD: 3500 } },
      ],
   },
]

const resolveCurrency = (currencyCode: string | undefined): SupportedCurrency => {
   const matchedCurrency = supportedCurrencies.find(supportedCurrency => supportedCurrency === currencyCode)
   return matchedCurrency || fallbackCurrency
}

/** The five sellable plans, priced for the org's currency (unknown/missing → USD). */
export const getStaticPlans = (currencyCode: string | undefined): ChargebeeDto.PurchasableItem[] => {
   const currency = resolveCurrency(currencyCode)

   return staticPlanDefinitions.map(definition => ({
      itemPriceId: `${definition.tierName.toLowerCase()}-${currency}-monthly`,
      itemId: definition.tierName,
      name: definition.tierName,
      externalName: definition.tierName,
      description: definition.description,
      features: definition.features,
      planType: definition.planType,
      price: definition.priceInCents[currency],
      currencyCode: currency,
      periodUnit: 'month',
      addons: definition.addons.map(addon => ({
         name: addon.name,
         price: addon.priceInCents[currency],
         currencyCode: currency,
         periodUnit: 'month',
      })),
   }))
}
